import React, { useMemo, useState } from "react";
import {
  Tooltip,
  Typography,
  styled,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import MUIButton from "../../../Component/UI/MUIButton/MUIButton";
import { OVERVIEW_LLM_LABELS } from "../../../utils/constant";
import { normalizeData } from "../../../utils/metric-sources";
import { ENTITY_LABELS } from "../../../utils/labelMapping";

const llmColorsMapping = {
  "gpt-4o": "#C7F0EC",
  Claude3: "#FF6F6F",
  Gemini: "#CEB4FF",
  llama3: "#FFCE6E",
  Perplexity: "#92B7FE",
  "llm-web": "#FEE7E6",
  "gpt-4o-web-search": "lightgreen",
  GeminiV25: "#CF9FFF",
  "gpt-5": "#B0E57C",
  "gpt-5-search-enabled": "#FFB6C1",
};

const colors = [
  "#C7F0EC",
  "#FF6F6F",
  "#CEB4FF",
  "#FFCE6E",
  "#92B7FE",
  "#FEE7E6",
  "#B0E57C",
  "#FFB6C1",
  "#ADD8E6",
  "#D8BFD8",
  "#FFD700",
  "#7FFFD4",
];

const fallbackColorMap = {};

const generateColorForLLM = (llm) => {
  if (!fallbackColorMap[llm]) {
    const colorIndex = Object.keys(fallbackColorMap).length % colors.length;
    fallbackColorMap[llm] = colors[colorIndex];
  }
  return fallbackColorMap[llm];
};

const processData = (data = [], sourceAlias = {}) => {
  if (!Array.isArray(data) || typeof sourceAlias !== "object") {
    return []; // Ensure function returns an empty array if inputs are invalid
  }

  return data
    .filter((obj) => obj && typeof obj === "object")
    .map((item) =>
      Object.fromEntries(
        Object.entries(item || {}).filter(
          ([_, value]) => value !== null && value !== undefined
        )
      )
    );
};

const ReTableView = ({
  data,
  sourceAlias = [],
  type,
  isValidated = true,
  selectedInput,
  title,
  dropdownOptions = [], // [{value,label}, ...]
  selectedDropdownValues = [], // array of values (strings)
  onDropdownChange = () => {},
  isOverview = false,
}) => {
  // keep original normalize + state
  data = normalizeData(data, selectedInput?.llm);
  const [visibleRows, setVisibleRows] = useState(15);

  const filteredData = useMemo(
    () => processData(data, sourceAlias),
    [data, sourceAlias]
  );

  const sortedData = useMemo(() => {
    return [...filteredData]
      .filter((obj) => !obj?.name.includes("vertexaisearch"))
      ?.sort((a, b) => {
        const totalA = Object.keys(a).reduce(
          (sum, key) =>
            key !== "name" && a[key]?.aliasTotalCount
              ? sum + Number(a[key]?.aliasTotalCount)
              : sum,
          0
        );
        const totalB = Object.keys(b).reduce(
          (sum, key) =>
            key !== "name" && b[key]?.aliasTotalCount
              ? sum + Number(b[key]?.aliasTotalCount)
              : sum,
          0
        );
        return totalB - totalA; // Descending order
      });
  }, [filteredData]);

  const allLLMKeys = useMemo(() => {
    const keysSet = new Set();

    filteredData?.forEach((item) => {
      Object.keys(item || {}).forEach((key) => {
        if (key !== "name") keysSet.add(key);
      });
    });

    return Array.from(keysSet);
  }, [filteredData]);

  const handleShowMore = () => {
    setVisibleRows((prev) => prev + 15);
  };

  const maxTotalValue = useMemo(() => {
    return Math.max(
      ...sortedData.map((item) =>
        Object.keys(item).reduce(
          (sum, key) =>
            key !== "name" && item[key]?.aliasTotalCount
              ? sum + Number(item[key]?.aliasTotalCount)
              : sum,
          0
        )
      ),
      1
    );
  }, [sortedData]);

  const renderProgressBar = (item) => {
    const totalValue = Object.keys(item).reduce(
      (sum, key) =>
        key !== "name" && item[key]?.aliasTotalCount
          ? sum + Number(item[key]?.aliasTotalCount)
          : sum,
      0
    );

    const barWidth = `${(totalValue / maxTotalValue) * 100}%`;
    return (
      <div
        style={{
          display: "flex",
          height: 10,
          borderRadius: 5,
          overflow: "hidden",
          width: barWidth,
          backgroundColor: "#e0e0e0",
          position: "relative",
          zIndex: 1,
        }}
      >
        {allLLMKeys?.map((llm, i) => (
          <Tooltip
            key={i}
            title={
              <div style={{ textAlign: "left" }}>
                <Typography style={{ fontSize: 14, fontWeight: "bold" }}>
                  {OVERVIEW_LLM_LABELS?.[llm] || llm}
                </Typography>
                <div
                  style={{
                    display: "inline-block",
                    width: 12,
                    height: 12,
                    backgroundColor:
                      llmColorsMapping?.[llm] || generateColorForLLM(llm),
                    marginRight: 5,
                    borderRadius: "50%",
                  }}
                />
                <Typography style={{ display: "inline", fontSize: 14 }}>
                  {item[llm]?.aliasTotalCount}
                </Typography>
              </div>
            }
            arrow
            placement="top"
          >
            <div
              style={{
                width: `${(item[llm]?.aliasTotalCount / totalValue) * 100}%`,
                backgroundColor:
                  llmColorsMapping?.[llm] || generateColorForLLM(llm),
                height: "100%",
                cursor: "pointer",
              }}
            />
          </Tooltip>
        ))}
      </div>
    );
  };

  const ScrollableTooltip = styled(({ className, ...props }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  ))({
    [`& .MuiTooltip-tooltip`]: {
      maxHeight: 300, // max height of tooltip
      overflowY: "auto", // enable vertical scroll
      maxWidth: 500, // optional: prevent it from being too wide
      wordBreak: "break-all",
      padding: 8,
    },
  });

  const renderSourceCell = (item) => {
    const aliases = Object.entries(item)
      .filter(([key]) => key !== "name")
      .flatMap(([_, llmData]) => llmData.aliases || []);
    const mergedAliasesMap = {};

    aliases.forEach((alias) => {
      const url = alias.source_alias_url;
      if (!url) return;

      if (!mergedAliasesMap[url]) {
        mergedAliasesMap[url] = { ...alias };
        mergedAliasesMap[url].alias_absolute_count =
          Number(alias.alias_absolute_count) || 0;
      } else {
        mergedAliasesMap[url].alias_absolute_count +=
          Number(alias.alias_absolute_count) || 0;

        if (
          alias.http_status === "200" ||
          mergedAliasesMap[url].http_status === "200"
        ) {
          mergedAliasesMap[url].http_status = "200";
        }
      }
    });

    let mergedAliases = Object.values(mergedAliasesMap);
    mergedAliases.sort(
      (a, b) => (b.alias_absolute_count || 0) - (a.alias_absolute_count || 0)
    );
    const isReporting = window.location.search.includes("isReporting=true");

    const isForbidden = mergedAliases.every(
      (alias) => alias.http_status !== "200"
    );
    return !isReporting ? (
      <ScrollableTooltip
        title={
          mergedAliases.length > 0 ? (
            <div>
              {mergedAliases.map((alias, index) => {
                const appeared = alias.alias_absolute_count || 0;
                return (
                  <div key={index} style={{ marginBottom: 8 }}>
                    {/* Full URL */}
                    <Typography
                      component="a"
                      href={
                        alias.http_status === "200"
                          ? alias.source_alias_url
                          : undefined
                      }
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{
                        display: "block",
                        color:
                          alias.http_status === "200" ? "white" : "lightgray",
                        textDecoration:
                          alias.http_status === "200" ? "underline" : "none",
                        cursor:
                          alias.http_status === "200"
                            ? "pointer"
                            : "not-allowed",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        marginBottom: 4,
                      }}
                    >
                      {alias.source_alias_url}
                    </Typography>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: "white", minWidth: "50px" }}
                      >
                        [{Math.round(appeared)} Citation/Identification count]
                      </Typography>
                    </Box>

                    {index < mergedAliases.length - 1 && (
                      <hr style={{ borderColor: "gray", margin: "6px 0" }} />
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            ""
          )
        }
        placement="top-start"
        arrow
        disableInteractive={false}
      >
        <Typography
          style={{
            color: isForbidden ? "gray" : "blue",
            cursor: isForbidden ? "not-allowed" : "pointer",
            textDecoration: isForbidden ? "none" : "underline",
          }}
        >
          {item.name}
        </Typography>
      </ScrollableTooltip>
    ) : (
        <Typography
          style={{
            color: isForbidden ? "gray" : "blue",
            cursor: isForbidden ? "not-allowed" : "pointer",
            textDecoration: isForbidden ? "none" : "underline",
          }}
        >
          {item.name}
        </Typography>
    );
  };

  const headers = [
    { label: "Source", width: "25%" },
    { label: "Source Frequency", width: "30%" },
  ];
  if (isValidated) {
    headers.push({ label: "Validated", width: "15%" });
  }

  // Dropdown rendering (multi-select) inside title area
const renderDropdown = () => {
  if (!dropdownOptions || dropdownOptions.length === 0) return null;

  // Ensure selectedDropdownValues is an array
  const selectedArr = Array.isArray(selectedDropdownValues)
    ? selectedDropdownValues
    : [];

  const handleChange = (event) => {
    const val = event.target.value;
    // MUI returns array for multiple select
    onDropdownChange(val);
  };

  const handleChipDelete = (valueToRemove) => (event) => {
    event.stopPropagation();
    event.preventDefault();
    
    const newSelectedValues = selectedArr.filter(val => val !== valueToRemove);
    onDropdownChange(newSelectedValues);
  };

  const renderSelectedChips = (selected) => {
    if (!selected || selected.length === 0) {
      return <span style={{ color: '#9ca3af' }}>None</span>;
    }

    return (
      <Box 
        sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 0.5,
          alignItems: 'center',
        }}
      >
        {selected.map((selectedValue) => {
          const option = dropdownOptions.find(opt => opt.value === selectedValue);
          const displayName = option ? option.label : selectedValue;

          return (
            <Chip
              key={selectedValue}
              label={displayName}
              size="small"
              onDelete={handleChipDelete(selectedValue)}
              onMouseDown={(e) => {
                e.stopPropagation();
              }}
              sx={{
                height: "auto",
                maxWidth: "100%",
                margin: "2px 0",
                "& .MuiChip-label": {
                  whiteSpace: "normal",
                  wordBreak: "break-word",
                  lineHeight: 1.3,
                  paddingTop: "4px",
                  paddingBottom: "4px",
                  paddingRight: "4px",
                },
                "& .MuiChip-deleteIcon": {
                  fontSize: "18px",
                  margin: "0 4px 0 -2px",
                }
              }}
            />
          );
        })}
      </Box>
    );
  };

  return (
    <FormControl size="small" sx={{ minWidth: 220 }}>
      <InputLabel sx={{ fontSize: 13 }}>Source Types</InputLabel>
      <Select
        multiple
        value={selectedArr}
        onChange={handleChange}
        renderValue={renderSelectedChips}
        label="Source Types"
        sx={{
          minHeight: "40px",
          "& .MuiSelect-select": {
            paddingTop: "6px",
            paddingBottom: "6px",
            display: "flex",
            alignItems: "center",
            minHeight: "28px",
          }
        }}
      >
        {dropdownOptions.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <Checkbox checked={selectedArr.indexOf(opt.value) > -1} />
            <ListItemText primary={opt.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

  return (
    <>
    
        <div
          className="mt-2 mb-2 d-flex align-items-center justify-content-center"
          style={{ gap: "10px", flexWrap: "wrap" }}
        >
         {!isOverview && (
          <h5 className="m-0 text-center">
            {title || `${type} Query Sources`}
          </h5>
         )}
          {/* dropdown placed next to the title */}
          {renderDropdown()}
        </div>
      <div
       id={(type === ENTITY_LABELS.brand ? "brand" : type === ENTITY_LABELS.product ? "product" : type.toLowerCase()) + "_id"}
        // id={type.toLowerCase() + "_id"}
        style={{
          maxHeight: "500px",
          overflowY: "auto",
          position: "relative",
        }}
      >
        <table className="table table-striped" style={{ width: "100%" }}>
          <thead
            style={{
              position: "sticky",
              top: 0,
              background: "white",
              zIndex: 2,
            }}
          >
            <tr className="prdtblHeader">
              {headers.map((header, index) => (
                <th
                  key={header.label}
                  style={{
                    width: header.width,
                    textAlign: index === 0 ? "left" : "center",
                    padding: "10px",
                  }}
                >
                  {header.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!!sortedData.length ? (
              sortedData.slice(0, visibleRows).map((item, index) => (
                <tr key={index}>
                  <td style={{ width: "25%", textAlign: "left" }}>
                    {renderSourceCell(item, sourceAlias[item.name] || [])}
                  </td>
                  <td style={{ width: "30%", textAlign: "center" }}>
                    {renderProgressBar(item)}
                  </td>
                  {isValidated ? (
                    <td style={{ width: "15%", textAlign: "center" }}>
                      {Object.entries(item)
                        .filter(([key]) => key !== "name")
                        .flatMap(([_, llmData]) => llmData.aliases || [])
                        ?.every((link) => link.http_status !== "200") ? (
                        <CancelIcon style={{ color: "#F59E0B" }} />
                      ) : (
                        <CheckCircleIcon style={{ color: "#36CFC2" }} />
                      )}
                    </td>
                  ) : null}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={4}
                  style={{ textAlign: "center", padding: "20px" }}
                >
                  No data available
                </td>
              </tr>
            )}
          </tbody>
        </table>
        {visibleRows < sortedData.length && (
          <div
            style={{ textAlign: "center", padding: "10px", fontWeight: "400" }}
          >
            <MUIButton variant="text" onClick={handleShowMore}>
              Load More
            </MUIButton>
          </div>
        )}
      </div>
    </>
  );
};

export default ReTableView;
